import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { errorMessage } from '../lib/api-error';
import { date } from '../lib/format';
import { BASIS_LABELS, type CalendarEntry } from '../lib/compliance-api';
import { Button, Input, Modal, Spinner, Textarea } from '../components/ui';

/**
 * ELLE SON GÜN — yalnız yöneticiye gösterilir (sunucu da yalnız ADMIN'e yazdırır).
 *
 * ⚠ KURAL PANELDE YOK. "Yazılımın tarihini ileri alan kayıt kaynaksız olamaz"
 * sunucuda ve şemada duruyor; panel yalnız SÖYLER. Kuralın panelde bir kopyası,
 * ayrıştığı gün sunucunun reddedeceği bir kaydı "geçerli" gösterirdi.
 *
 * ⚠ Form kalemin anahtarıyla (`key`) açılır: başka bir kaleme geçildiğinde
 * önceki kalemin yarım kalmış gerekçesi taşınmaz.
 */
export function DueDateOverrideModal(props: { entry: CalendarEntry; onClose: () => void }) {
  const { entry, onClose } = props;
  const qc = useQueryClient();
  const [dueDate, setDueDate] = useState(entry.dueDate ?? '');
  const [reason, setReason] = useState(entry.overrideReason ?? '');
  const [sourceUrl, setSourceUrl] = useState(entry.overrideSourceUrl ?? '');
  const [hata, setHata] = useState<string | null>(null);

  /* Yazılımın kendi hesabı: elle girilmişse yerine geçtiği tarih, değilse gösterilen tarih. */
  const yazilimTarihi = entry.overridden ? entry.replacedDueDate : entry.dueDate;

  const bitti = async () => {
    await qc.invalidateQueries({ queryKey: ['compliance'] });
    onClose();
  };

  const kaydet = useMutation({
    mutationFn: () =>
      api.put<unknown>('/compliance/due-date-overrides', {
        obligationKind: entry.kind,
        periodKey: entry.periodKey,
        dueDate,
        reason,
        sourceUrl: sourceUrl.trim() === '' ? undefined : sourceUrl.trim(),
      }),
    onSuccess: bitti,
    onError: (e: unknown) => setHata(errorMessage(e)),
  });

  const kaldir = useMutation({
    mutationFn: () =>
      api.delete<unknown>(
        `/compliance/due-date-overrides/${encodeURIComponent(entry.kind)}/${encodeURIComponent(entry.periodKey)}`,
      ),
    onSuccess: bitti,
    onError: (e: unknown) => setHata(errorMessage(e)),
  });

  const mesgul = kaydet.isPending || kaldir.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      title={entry.overridden ? 'Elle girilen son günü değiştir' : 'Son günü elle gir'}
      footer={
        <>
          {mesgul && <Spinner />}
          {entry.overridden && (
            <Button
              variant="ghost"
              disabled={mesgul}
              onClick={() => {
                setHata(null);
                kaldir.mutate();
              }}
            >
              Kaldır — yazılımın tarihine dön
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            disabled={mesgul}
            onClick={() => {
              setHata(null);
              kaydet.mutate();
            }}
          >
            Kaydet
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm">
          <strong>{entry.label}</strong> <span className="font-mono text-xs">{entry.periodKey}</span>
        </p>
        <p className="text-xs text-[var(--c-ink-soft)]">
          Yazılımın hesabı:{' '}
          {yazilimTarihi ? <strong>{date(yazilimTarihi)}</strong> : `yok — ${BASIS_LABELS[entry.basis]}`}
        </p>

        <Input label="Son gün" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        <Textarea
          label="Gerekçe"
          value={reason}
          placeholder="Örn. FOD, 2025 müşteri listesinin süresini 30 Nisan 2026'ya uzattı"
          hint="Zorunlu. Bu tarihin nereden geldiği yazılımda durmalı."
          onChange={(e) => setReason(e.target.value)}
        />
        <Input
          label="Kaynak bağlantısı"
          value={sourceUrl}
          placeholder="https://financien.belgium.be/..."
          hint="Yazılımın hesabından SONRA bir tarih giriyorsanız zorunlu: sizi geç bırakabilecek tek yön bu."
          onChange={(e) => setSourceUrl(e.target.value)}
        />

        <p className="rounded-[var(--r-md)] border border-[var(--c-border)] px-3 py-2 text-xs text-[var(--c-ink-soft)]">
          Takvim bu tarihi <strong>“elle girildi”</strong> işaretiyle ve yerine geçtiği tarihle
          gösterir; hatırlatmalar yeni tarihe göre yeniden kurulur. Kayıt ve gerekçe denetim
          kaydına düşer.
        </p>
        {hata && <p className="text-xs text-[var(--c-danger)]">{hata}</p>}
      </div>
    </Modal>
  );
}
